import { useCallback } from "react"
import { apiFetch } from "../../../utils/apiFetch"

export default function usePatientWorkspaceController({
  API_BASE,
  clinicId,
  selectedAppointment,
  patientSnapshot,
  patientOdontogram,
  odontogramForm,
  setCreatingOdontogramEntry,
  setOdontogramForm,
  setPatientAlerts,
  setLoadingPatientAlerts,
  setPatientWorkspaceOpen,
  setPatientWorkspaceTab,
  loadPatientSnapshot,
  loadPatientTreatmentPlans,
  loadPatientOdontogram
}) {
  const resolveWorkspacePatientId = useCallback(() => {
    return (
      selectedAppointment?.patientId ||
      patientSnapshot?.patient?.id ||
      patientOdontogram?.patient?.id ||
      ""
    )
  }, [selectedAppointment, patientSnapshot, patientOdontogram])

  const loadPatientAlerts = useCallback(async (patientId) => {
    try {
      if (!patientId || !clinicId || clinicId === "ALL") {
        setPatientAlerts([])
        return
      }

      setLoadingPatientAlerts(true)

      const params = new URLSearchParams({
        patientId,
        clinicId
      })

      const res = await apiFetch(
        `${API_BASE}/appointments/clinical-alerts?${params.toString()}`
      )
      const data = await res.json().catch(() => null)

      setPatientAlerts(res.ok && Array.isArray(data) ? data : [])
    } catch {
      setPatientAlerts([])
    } finally {
      setLoadingPatientAlerts(false)
    }
  }, [
    API_BASE,
    clinicId,
    setPatientAlerts,
    setLoadingPatientAlerts
  ])

  const createOdontogramEntry = useCallback(async () => {
    const patientId = resolveWorkspacePatientId()

    if (!patientId) {
      alert("No se encontró el paciente")
      return
    }

    if (!odontogramForm.tooth || !odontogramForm.condition) {
      alert("La pieza y la condición son obligatorias")
      return
    }

    try {
      setCreatingOdontogramEntry(true)

      const res = await apiFetch(`${API_BASE}/appointments/odontogram-entry`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          patientId,
          clinicId,
          tooth: Number(odontogramForm.tooth),
          surface: odontogramForm.surface || undefined,
          condition: odontogramForm.condition,
          status: odontogramForm.status || "ACTIVE"
        })
      })

      const data = await res.json().catch(() => null)

      if (!res.ok) {
        alert(data?.message || "No se pudo crear la entrada en odontograma")
        return
      }

      alert("Entrada de odontograma creada")

      setOdontogramForm({
        tooth: "",
        surface: "ALL",
        condition: "Caries",
        status: "ACTIVE"
      })

      await loadPatientOdontogram(patientId)
      await loadPatientSnapshot(patientId)
    } catch {
      alert("Error al crear entrada de odontograma")
    } finally {
      setCreatingOdontogramEntry(false)
    }
  }, [
    API_BASE,
    clinicId,
    odontogramForm,
    resolveWorkspacePatientId,
    setCreatingOdontogramEntry,
    setOdontogramForm,
    loadPatientOdontogram,
    loadPatientSnapshot
  ])

  const handlePatientAlertAction = useCallback((alert) => {
    const actionType = alert?.actionType
    const payload = alert?.payload || {}

    if (!actionType) return

    if (actionType === "OPEN_FINANCE") {
      if (!payload.patientId || !payload.clinicId) return

      window.location.href = `/patients/${payload.patientId}?clinicId=${payload.clinicId}&tab=FINANCE`
      return
    }

    if (actionType === "OPEN_PLAN") {
      if (!payload.patientId || !payload.clinicId) return

      if (payload.treatmentPlanItemId || payload.treatmentId || payload.tooth) {
        localStorage.setItem(
          "odontoflow_plan_prefill",
          JSON.stringify({
            patientId: payload.patientId,
            clinicId: payload.clinicId,
            tooth: payload.tooth ?? null,
            surface: payload.surface || "ALL",
            condition: payload.condition || ""
          })
        )
      }

      window.location.href = `/treatment-plans?patientId=${payload.patientId}&clinicId=${payload.clinicId}`
      return
    }

    if (actionType === "SCHEDULE_RECALL" || actionType === "SCHEDULE_TREATMENT") {
      if (!payload.patientId || !payload.clinicId) return

      localStorage.setItem(
        "odontoflow_new_appointment_context",
        JSON.stringify({
          patientId: payload.patientId,
          clinicId: payload.clinicId,
          treatmentId: payload.treatmentId || "",
          treatmentPlanItemId: payload.treatmentPlanItemId || "",
          tooth: payload.tooth ?? null,
          surface: payload.surface || "ALL",
          source: "clinical_alert"
        })
      )

      window.location.href = "/agenda"
    }
  }, [])

  const openPatientWorkspace = useCallback(async (tab) => {
    const patientId = resolveWorkspacePatientId()

    if (!patientId) {
      alert("No se encontró el paciente de esta cita")
      return
    }

    setPatientWorkspaceOpen(true)
    setPatientWorkspaceTab(tab)

    await loadPatientAlerts(patientId)

    if (tab === "expediente") {
      await loadPatientSnapshot(patientId)
      await loadPatientTreatmentPlans(patientId)
      return
    }

    if (tab === "odontograma") {
      await loadPatientSnapshot(patientId)
      await loadPatientOdontogram(patientId)
    }
  }, [
    resolveWorkspacePatientId,
    setPatientWorkspaceOpen,
    setPatientWorkspaceTab,
    loadPatientAlerts,
    loadPatientSnapshot,
    loadPatientTreatmentPlans,
    loadPatientOdontogram
  ])

  return {
    loadPatientAlerts,
    createOdontogramEntry,
    handlePatientAlertAction,
    openPatientWorkspace
  }
}